import { IScope, ILogService, IWindowService, IQService, IPromise } from "angular";
import { StateService, StateParams } from "@uirouter/angularjs";
import { IModalStackService } from "angular-ui-bootstrap";
import { AuthenticationResolverService } from "@gtmhub/auth";
import { IError, ErrorService } from "./services";
import { UIErrorHandlingService } from "@gtmhub/error-handling";
import { InsightService } from "../insights/services/insightService";
import { IInsightboard } from "../insightboards/models/models";
import { IInsight } from "../insights/models/models";
import { ICollection } from "../models";
import { Intercom } from "./intercom";
import { MetricService, IMetric } from "@gtmhub/okrs/metrics";

interface IErrorScope extends IScope {
  error: IError;
  userDisplayName: string;
  showIntercom(): void;
  goHome(): void;
  logout(): void;
}

export class ErrorCtrl {
  public static $inject = ["$scope", "$uibModalStack", "$stateParams", "ErrorService", "$log", "AuthenticationResolverService", "$window", "UIErrorHandlingService"];

  constructor(
    private $scope: IErrorScope,
    private $uibModalStack: IModalStackService,
    private $stateParams: StateParams,
    private errorService: ErrorService,
    private $log: ILogService,
    private authenticationResolverService: AuthenticationResolverService,
    private $window: IWindowService,
    private uiErrorHandlingService: UIErrorHandlingService
  ) {
    this.$uibModalStack.dismissAll();

    this.$scope.goHome = this.goHome;
    this.$scope.showIntercom = this.showIntercom;
    this.$scope.logout = this.logout;
    this.$scope.userDisplayName = this.authenticationResolverService.getUserDisplayNameFromIdToken();

    if (this.$stateParams.error) {
      this.$scope.error = this.$stateParams.error;

      if (this.$scope.error.data && this.$scope.error.data.config && this.$scope.error.data.config.headers && this.$scope.error.data.config.headers.Authorization) {
        delete this.$scope.error.data.config.headers.Authorization;
      }
    } else {
      this.$scope.error = this.errorService.getStoredError();
    }

    // we should redirect away from the login screen if there is no error to report like when you navigate back to the error page.
    if (!this.$scope.error) {
      this.goHome();
    }

    this.$log.error(this.$scope.error);
    this.uiErrorHandlingService.trackError("page");
  }

  private showIntercom = () => {
    Intercom("show");
  };

  // go big or
  private goHome = () => {
    this.$window.open("/login", "_self");
  };

  private logout = (): void => {
    this.authenticationResolverService.logout({ ssoLogoutIfEnabled: true });
  };
}

export interface IDispatchParams {
  itemType: string;
  itemID: string;
  target: string;
  targetID: string;
}

export class DispatchCtrl {
  public static $inject = ["$state", "$stateParams", "$q", "UIErrorHandlingService", "MetricService", "InsightService"];

  constructor(
    private $state: StateService,
    private $stateParams: IDispatchParams,
    private $q: IQService,
    private uiErrorHandlingService: UIErrorHandlingService,
    private metricService: MetricService,
    private insightService: InsightService
  ) {
    this.redirect();
  }

  private redirect = () => {
    switch (this.$stateParams.itemType) {
      case "comment":
        this.redirectComment();
        break;
      // this seems to be no longer/not set by BE as targetType. leaving it here only because there might be some notifications containing it
      case "note":
        this.$state.go("gtmhub.home._metric.metric", { metricId: this.$stateParams.targetID, returnState: "gtmhub.home" });
        break;
      default:
        this.$state.go("gtmhub.home");
    }
  };

  private redirectComment = () => {
    switch (this.$stateParams.target) {
      case "insight": {
        const insightboardPromise = this.insightService.getUsageOnBoards(this.$stateParams.targetID);
        const insightPromise = this.getTargetInsight();
        this.$q
          .all({
            insightboard: insightboardPromise,
            insight: insightPromise,
          })
          .then(
            (resolvedPromiseMap: { insightboard: IInsightboard[]; insight: IInsight }) => {
              const insightBoardId: string = resolvedPromiseMap.insightboard[0].id;
              const insightName: string = resolvedPromiseMap.insight.name;
              this.$state.go("gtmhub.insightboard._comments.timeline", {
                dashboardId: insightBoardId,
                insightUniqueName: insightName,
                returnState: "gtmhub.insightboard",
              });
            },
            (err) => this.uiErrorHandlingService.handleModal(err)
          );

        break;
      }
      case "employee":
        this.$state.go("gtmhub.employees.employee", { employeeId: this.$stateParams.targetID, returnState: "gtmhub.employees" });
        break;
      case "team":
        this.$state.go("gtmhub.teams.team", { teamId: this.$stateParams.targetID, returnState: "gtmhub.teams" });
        break;
      case "goal":
        this.$state.go("gtmhub.home._goalSidebar.goal", { id: this.$stateParams.targetID, returnState: "gtmhub.home" });
        break;
      case "metric":
        this.$state.go("gtmhub.home._metric.metric", { metricId: this.$stateParams.targetID, returnState: "gtmhub.home" });
        break;
      case "checkin":
        this.openMetricMessageThread(this.$stateParams.targetID, this.$stateParams.target);
        break;
      case "checkinMention":
        this.openMetricMessageThread(this.$stateParams.targetID, this.$stateParams.target);
        break;
      case "task":
        this.$state.go("gtmhub.tasks._task.task", { taskId: this.$stateParams.targetID, returnState: "gtmhub.tasks" });
        break;
      default:
        this.$state.go("gtmhub.home");
    }
  };

  private openMetricMessageThread = (targetId: string, target: "checkin" | "checkinMention") => {
    this.getMetricByCheckin(targetId).then((metric) => {
      this.$state.go("gtmhub.home._metric.metric._message.thread", {
        metricId: metric.id,
        targetType: target,
        targetId: targetId,
        returnState: "gtmhub.home._metric.metric",
      });
    });
  };

  private getMetricByCheckin = (id: string): IPromise<IMetric> => {
    const deferred = this.$q.defer<IMetric>();
    this.metricService.getMetricByCheckin(id).then(
      (metric) => {
        deferred.resolve(metric);
      },
      (err) => {
        deferred.reject();
        this.uiErrorHandlingService.handleModal(err);
      }
    );

    return deferred.promise;
  };

  private getTargetInsight = (): IPromise<IInsight> => {
    const fields = "name";
    const deferred = this.$q.defer<IInsight>();

    this.insightService.getInsightsSafe(this.$stateParams.targetID, fields).then(
      (insights: ICollection<IInsight>) => {
        deferred.resolve(insights.items[0]);
      },
      (err) => {
        deferred.reject();
        this.uiErrorHandlingService.handleModal(err);
      }
    );

    return deferred.promise;
  };
}
